import React, {useState, useCallback, useRef} from "react";
import {useDropzone} from "react-dropzone";
import {useForm} from "react-hook-form";

import Button from "@material-ui/core/Button";
import Card from "@material-ui/core/Card/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";

import {
  APOLLO_GROUP_OPERATOR,
  APOLLO_PROFILE_SYSTEM_ADMIN,
  IsAuthorization,
} from "../../atom/Apollo_Authorization";
import {
  checkInvalidCharForImageFileName,
} from "../../common/Apollo_CommonUtil";
import {ApolloMessage, CustomizedSnackbars} from "../../atom/Apollo_Mesasge";
import {
  apollo_useStyles,
  options_state,
  options_state_edit,
  options_reviewstatus_ad_client_icon,
  options_reviewreason_ad_client_icon,
} from "../../atom/Apollo_styles";
import {ApolloBasicSelect, ApolloBasicTextField} from "../../molecule/Apollo_BaseItem";
import {AdClientIcon, AdClientIconInfo} from "./AccountModel"

export const AdClientIconProcessType = {
  create: "create",
  edit: "edit",
}

const maxFileSize: number = 300;

type AccountIconProp = {
  accountId: number,
  accountName: string,
  adClientIconState?: {
    adClientIcon: AdClientIcon,
    setAdClientIcon: any,
  },
  adClientIconInfo?: AdClientIconInfo,
  submitAction: any,
  goBackAction: any,
  processType: string;
}

/** 
 * 画面名：広告主アカウント作成/編集（共通部品）
 */
const AdClientIconFormApollo = ({
  accountId,
  accountName,
  adClientIconState,
  adClientIconInfo,
  submitAction,
  goBackAction,
  processType,
}: AccountIconProp) => {

  const classes = apollo_useStyles();
  const {data, setmsg} = React.useContext(ApolloMessage);

  const [dataURI, setDataURI] = useState(adClientIconInfo?.imageUrl ?? "");
  const [status, setStatus] = useState(adClientIconInfo?.status ?? 2);
  const [imageWidth, setImageWidth] = useState(0);
  const [imageHeight, setImageHeight] = useState(0);
  const [imageName, setImageName] = useState("");
  const [errorMessages, setErrorMessages] = useState("");
  const processing = useRef(false);
  const isAuthorization = IsAuthorization(APOLLO_GROUP_OPERATOR, APOLLO_PROFILE_SYSTEM_ADMIN);

  const reader = new FileReader();

  React.useEffect(() => {
    if (processType === AdClientIconProcessType.edit) {
      const image = new Image();
      image.src = adClientIconInfo?.imageUrl ?? ""
      image.onload = () => {
        setImageWidth(image.width)
        setImageHeight(image.height)
      }
    }
  }, []);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {

    adClientIconState?.setAdClientIcon({
      ...adClientIconState.adClientIcon,
      attachment: "",
      attachmentName: "",
    })
    setImageWidth(0);
    setImageHeight(0);
    setImageName("");
    setDataURI("");

    if (fileRejections[0]?.errors[0].code == 'file-invalid-type') {
      setmsg({
        type: "setMessage",
        payload: {
          ...data,
          open: true,
          type: "error",
          message: "画像ファイルは、jpg,jpeg,gifのいずれかのみ設定可能です。"
        }
      });
      return
    }

    if (fileRejections[0]?.errors[0].code == 'file-too-large') {
      setmsg({
        type: "setMessage",
        payload: {
          ...data,
          open: true,
          type: "error",
          message: `${fileRejections[0]?.file.size.toLocaleString()}バイトです。307,200バイト（300キロバイト）以下となるように設定してください。`
        }
      });
      return
    }

    if (!acceptedFiles[0]) {
      setmsg({
        type: "setMessage",
        payload: {
          ...data,
          open: true,
          type: "error",
          message: "アイコン画像のアップロードに失敗しました。"
        }
      });
      return
    }

    let dataUri = '';
    const fileNameOrigin: string = acceptedFiles[0].name
    const fileNameArray: string[] = fileNameOrigin.split('.')
    fileNameArray.pop()
    const fileName: string = fileNameArray.join(".")
    // ファイル名称禁止文字判定
    const imgNameErrorMsg = checkInvalidCharForImageFileName(fileName)
    if (imgNameErrorMsg !== "") {
      setmsg({
        type: "setMessage",
        payload: {
          ...data,
          open: true,
          type: "error",
          message: "画像ファイル名エラー：" + imgNameErrorMsg
        }
      });
      return
    }

    reader.onload = () => {
      dataUri = reader.result as string;

      const img = new Image();

      img.addEventListener("load", () => {
        const width = img.width;
        const height = img.height;
        // 比率判定
        if ((Math.round(width * 10 / height) / 10) !== 1) {
          setmsg({
            type: "setMessage",
            payload: {
              ...data,
              open: true,
              type: "error",
              message: "画像ファイルの縦横比は１：１のみ設定可能です。"
            }
          });
          return
        }

        adClientIconState?.setAdClientIcon({
          ...adClientIconState.adClientIcon,
          attachmentName: acceptedFiles[0].name,
          attachment: acceptedFiles[0],
        })
        setImageWidth(width);
        setImageHeight(height);
        setImageName(acceptedFiles[0].name);
        setDataURI(dataUri);
      });
      img.src = dataUri;
    }
    if (acceptedFiles[0]) {
      reader.readAsDataURL(acceptedFiles[0]);
    }
  }, [adClientIconState?.adClientIcon]);
  const {acceptedFiles, getRootProps, getInputProps} = useDropzone({
    accept: '.jpg, .jpeg, .gif',
    maxSize: 1024*maxFileSize,
    multiple: false,
    onDrop,
  });
  // // @ts-ignore
  // const files = acceptedFiles?.map(file => (
  //   file.name
  // ));

  const {
    register,
    handleSubmit,
    formState,
    control,
    errors,
  } = useForm({reValidateMode: 'onBlur'});
  const onSubmit = handleSubmit(async (formData: any) => {
    if (processing.current) return;
    if (formState.isSubmitting) return;

    if (processType === AdClientIconProcessType.create) {
      if (!adClientIconState?.adClientIcon.attachment) {
        setmsg({
          type: "setMessage",
          payload: {
            ...data,
            open: true,
            type: "error",
            message: "アイコン画像ファイルを設定してください。"
          }
        });
        return
      }
    }

    if (errorMessages) {
      setmsg({
        type: "setMessage",
        payload: {
          ...data,
          open: true,
          type: "error",
          message: errorMessages
        }
      });
      return
    }

    processing.current = true;
    await submitAction({
      ...formData,
      status: status,
    });
    processing.current = false;
  })

  return (
    <React.Fragment>
      <form noValidate onSubmit={onSubmit}>
        <Card className={classes.root} square variant="outlined">
          <CardContent>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="accountId"
                  label="広告主アカウントID"
                  value={accountId}
                  inputProps={{readOnly: true,}}
                  inputRef={register}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="accountName"
                  label="広告主アカウント名"
                  value={accountName}
                  inputProps={{readOnly: true,}}
                  inputRef={register}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicSelect
                  id="status"
                  label="ステータス"
                  options={processType === AdClientIconProcessType.edit
                    ? options_state_edit
                    : options_state
                  }
                  value={status}
                  defaultValue={processType === AdClientIconProcessType.edit
                    ? options_state_edit[1].value
                    : options_state[1].value
                  }
                  readOnly={false}
                  control={control}
                  errors={errors.status}
                  setState={(e: any) => setStatus(e)}
                />
              </Grid>
              <Grid item xs={12}>
                <Grid container xs={12}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle1" gutterBottom component="div" color="inherit">
                      アップロードファイル
                    </Typography>
                    {processType === AdClientIconProcessType.edit
                      ? undefined
                      : <React.Fragment>
                        <section className="container">
                          <div {...getRootProps()}>
                            <input {...getInputProps()} />
                            <div className={classes.droparea}>
                              <p className={classes.p}>ここにファイルをドラッグ＆ドロップ<br/>もしくは、クリックして選択</p>
                            </div>
                          </div>
                        </section>
                        <Typography variant="body2" gutterBottom component="div" color="textSecondary">
                          【サイズ】最大{maxFileSize}KB<br/>
                          【ファイル形式】jpg, jpeg, gif<br/>
                          【幅:高さ】1:1
                        </Typography>
                        </React.Fragment>
                    }
                    <aside style={{marginTop: '1em'}}>
                      <img src={dataURI} style={{width: '40%'}}/>
                    </aside>
                  </Grid>
                  {processType === AdClientIconProcessType.edit
                    ? <Grid item xs={6}>
                      <Typography variant="subtitle1" gutterBottom component="div" color="inherit">
                        アイコンイメージ
                      </Typography>
                      <aside style={{marginTop: '1em'}}>
                        <img src={dataURI} style={{width: '40%', borderRadius: '50%', borderStyle: 'dotted'}}/>
                      </aside>
                    </Grid>
                    : <Grid item xs={6} style={{paddingLeft: '5em'}}>
                      <Typography variant="subtitle1" gutterBottom component="div" color="inherit">
                        アイコンイメージ　※配信時には点線は表示されません。
                      </Typography>
                      <aside style={{marginTop: '1em'}}>
                        {dataURI
                          ? <img src={dataURI} style={{width: '40%', borderRadius: '50%', borderStyle: 'dotted'}}/>
                          : <div style={{width: '40%', aspectRatio: '1/1', borderRadius: '50%', borderStyle: 'dotted'}}></div>
                        }
                      </aside>
                    </Grid>
                  }
                </Grid>
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="width"
                  label="幅（画像選択後自動入力）"
                  variant="filled"
                  value={imageWidth}
                  inputProps={{type: 'number', readOnly: true,}}
                  inputRef={register}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="height"
                  label="高さ（画像選択後自動入力）"
                  variant="filled"
                  value={imageHeight}
                  inputProps={{type: 'number', readOnly: true,}}
                  inputRef={register}
                />
              </Grid>
              <Grid item xs={12}>
                <ApolloBasicTextField
                  required
                  id="iconFileName"
                  label="ファイル名（画像選択後自動入力）"
                  variant="filled"
                  value={adClientIconInfo?.adClientIconName ?? imageName}
                  inputProps={{maxLength: 255, readOnly: true,}}
                  inputRef={register}
                />
              </Grid>
              {processType === AdClientIconProcessType.edit
                ? <Grid item xs={12}>
                  <ApolloBasicSelect
                    id="approval"
                    label="審査ステータス"
                    options={options_reviewstatus_ad_client_icon}
                    value={adClientIconInfo?.approval}
                    control={control}
                    errors={errors}
                    setState={(e: any) => {}}
                    disabled={true}
                  />
                </Grid>
                : <></>
              }
              {processType === AdClientIconProcessType.edit && adClientIconInfo?.approval === 3
                ? <Grid item xs={12}>
                  <ApolloBasicSelect
                    id="denialReason"
                    label="否認理由"
                    options={options_reviewreason_ad_client_icon}
                    value={adClientIconInfo?.denialReason}
                    disabled={true}
                    readOnly={false}
                    control={control}
                    errors={errors}
                    setState={(e: number) => {}}
                  />
                </Grid>
                : <></>
              }
              {processType === AdClientIconProcessType.edit && adClientIconInfo?.approval === 3
                ? <Grid item xs={12}>
                  <ApolloBasicTextField
                    id="denialReasonDetail"
                    label="否認詳細"
                    value={adClientIconInfo?.denialReasonDetail}
                    error={errors.denialReasonDetail}
                    disabled={true}
                    inputRef={register({
                      maxLength: {
                        value: 255,
                        message: "255文字以内で入力して下さい。"
                      },
                    })}
                  />
                </Grid>
                : <></>
              }
            </Grid>
          </CardContent>
          <CardActions>
            {isAuthorization
              ? <Button
                type="submit"
                color="primary"
                className={classes.button}
                onClick={onSubmit}
                variant="contained"
                disabled={adClientIconState?.adClientIcon.attachment === "" || processing.current}
              >
                {processType === AdClientIconProcessType.edit ? "更新": "作成"}
              </Button>
              : undefined
            }
            <Button
              variant="contained"
              color="inherit"
              onClick={goBackAction}
              className={classes.button}
              disabled={processing.current}
            >
              キャンセル
            </Button>
          </CardActions>
        </Card>
      </form>
    </React.Fragment>
  );
}
export default AdClientIconFormApollo;
